import React, { useState, useEffect, useCallback, forwardRef, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
// import _ from "lodash";
import { Tooltip } from '@progress/kendo-react-tooltip';

import "./Detail.scss";
import BaseGrid from "../grid/Grid";
import Grid from "../grid/GridWrapper";
import DatePicker from "../forms/DatePicker";
import DropDownList from "../forms/DropDownList";
import Input from "../forms/Input";
import LoadingPanel from "../LoadingPanel";
import NumericTextBox from "../forms/NumericTextBox";
import UnitNumericTextBox from "../forms/UnitNumericTextBox";
import Picker from "../forms/Picker";
import AutocompleteComponent from "../forms/Autocomplete";
import MultiSelect from "../forms/MultiSelect";
import Switch from "../forms/Switch";
import Textarea from "../forms/Textarea";
import TimePicker from "../forms/TimePicker";
import Conversational from "../conversational/Conversational";
import DateTimePicker from "../forms/DateTimePicker";
import DateRangePicker from "../forms/DateRangePicker";
import RadioGroup from "../forms/RadioGroup";
import ButtonGroup from "../buttons/ButtonGroup";
import Toolbar from "./Toolbar";
import Upload from "../forms/Upload";
import ValidationMessage from "./ValidationMessage";
import ValidationAlert from "../ValidationAlert";
import FileContent from "../dialogs/FileContent";
import withRequests from "../utils/withRequests";
import Checkbox from "../forms/Checkbox";
import MultiPicker from "../forms/MultiPicker";
import FileDialog from "../dialogs/FileDialog"
import FormDataTable from "../forms/FormDataTable";

// import DateInput from '../../test/DatePickerWrapper/DateInput';
import DateInput from "../forms/DateInput";
import DateRangeInput from '../forms/DateRangeInput';

/**
 * @typedef {Object} detailSchema
 * @property {string} field - Unique field identifier
 * @property {string} title - Field name
 * @property {string} type - Field type - "string", "boolean", "enum", "date", "time", "datetime", "text", "numeric", "container", "files", "password", "fileupload", "filepreview"
 * @property {bool} required - Required
 * @property {bool} disabled - Readonly
 * @property {bool} autofocus - Show cursor in field?
 * @property {number} tabIndex - Index of the tab
 * @property {string} defaultValue - Default value
 * @property {string} validationMessage - Validation Message
 * @property {string} tooltip - Tooltip over the column
 * @property {bool} recommended - Recommended
 * @property {element} customComponent - Component displayed after the field.
 * @param {bool} validationIcon - Show validation icon?
 */

/**
 * Detail component
 * @module
 * @param {detailSchema[]} schema - Schema
 * @param {object} data - Data
 * @param {func} onChange - On field change function
 * @param {func} onBlur - On field blur function
 * @param {func} handleSubmit - Handle sumbit function
 * @param {func} handleBlur - Invokes on field blur, function
 * @param {func} getValue - Get field value, function
 * @param {func} onUpload - On file upload function
 * @param {object} parentProps - Parent props
 * @param {bool} disabled - Read only detail
 * @param {number} akcDisabled - Disabled actions mask number
 * @param {number} akcHidden - Hidden actions mask number
 * @param {bool} isLoading - Is loading
 * @param {array} ui - Custom schema
 * @param {object} args - Custom arguments
 * @param {element} toolbarActions - Toolbar actions
 * @param {string} primaryField - Primary field
 * @param {bool} toolbarHidden - Toolbar hidden
 * @param {bool} autofocus - Show cursor in field?
 * @param {func} next - Switches to the next entry function
 * @param {func} previous - Switches to the previous entry function
 * @param {bool} disabledNext - Is next entry disabled?
 * @param {bool} disabledPrevious - Is previous entry disabled?
 * @param {element} currentEl - Current element
 * @param {func} onBack - Function invoked after click on back arrow
 * @param {object} initFilter - Init filter
 * @param {object} fixedFilter - Fixed filter
 * @param {func} onAdd - On entry add function
 * @param {func} onGet - Get item function
 * @param {bool} isPicker - Is field picker
 * @param {bool} hiddenBack - Hidden back arrow in detail toolbar
 * @param {func} applyFilter - Apply filter function
 * @param {func} componentFactory - Factory for custom components
 * @param {string} title - Detail title
 * @param {array} pages - Pages
 * @param {bool} hiddenSave - Hidden save button in detail toolbar
 * @param {bool} sidebarHorizontal - Should sidebar be horizontal?
 * @param {string} [db] - db
 */

const Detail = forwardRef((props, ref) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [ui, setUi] = useState(null);
  const tooltipRef = useRef(null);
  const [openTooltip, setOpenTooltip] = React.useState(false);
  const [tooltipElement, setTooltipElement] = React.useState(null);

  const getPageOffset = useCallback(() => {
    return props.pages && props.pages.find((x) => x.IZalTyp === 2) ? 0 : 1;
  }, [props.pages]);

  const buildPageTitle = useCallback((pageIndex) => {
    const offset = getPageOffset();
    if (props.pages && props.pages[pageIndex - offset]) {
      return props.pages[pageIndex - offset].title;
    }

    switch (pageIndex) {
      case 1:
        return "Doplňujúce informácie";
      case 2:
        if (props.pageTitle) {
          return props.pageTitle;
        } else {
          return "Workflow";
        }
      default:
        return "Neznámy index strany";
    }
  }, [props.pages, props.pageTitle, getPageOffset]);

  const buildPageCustomClass = useCallback((pageIndex) => {
    const offset = getPageOffset();
    if (props.pages && props.pages[pageIndex - offset]) {
      const customClass = props.pages[pageIndex - offset].customClass;
      return customClass || "";
    }
    return "";
  }, [getPageOffset, props.pages]);


  const getUI = useCallback(() => {
    const ui = props.ui;
    const pages = props.pages;
    const offset = getPageOffset();

    return ui
      ? ui.map((page, pageIndex) => {
        return {
          page,
          title: buildPageTitle(pageIndex),
          customClass: buildPageCustomClass(pageIndex),
          IZobraz: offset === 1 ? 1 : pages[pageIndex].IZobraz,
          IZalTyp: offset === 1 ? 1 : pages[pageIndex].IZalTyp,
        };
      })
      : null;
  }, [props.ui, buildPageTitle, buildPageCustomClass, getPageOffset, props.pages]);

  useEffect(() => {
    let newIndex = 0;
    if (props.pages) {
      newIndex = props.pages.findIndex((page) => page.active) + 1;
    }
    setPageIndex(newIndex);
  }, [props.pages]);

  useEffect(() => {
    let updatedUI = getUI();
    setUi(updatedUI);
  }, [props.ui, getUI]);

  const handleClick = (event, pageIndexNew) => {
    setPageIndex(pageIndex === pageIndexNew ? 0 : pageIndexNew);
  };

  const handleSelect = (event) => {
    setTabIndex(event.selected);
  };

  const buildInput = (x, page) => {
    switch (x.type) {
      case "files":
      case "fileupload":
        return (
          <Upload
            name={x.field}
            onChange={props.onChange}
            onUpload={x.onUpload || props.onUpload}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            url={x.url}
            restrictions={{
              allowedExtensions: x.restrictions != null ? x.restrictions : [],
              maxFileSize: x.maxFileSize,
            }}
            data={x.data}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            autoUpload={x.autoUpload}
            multiple={x.multiple}
            showActionButtons={x.showActionButtons}
            requiredMaximumSize={x.requiredMaximumSize}
            listItemUI={x.listItem}
          />
        );

      case "boolean":
        return (
          <Switch
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            onLabel={x.onLabel}
            offLabel={x.offLabel}
            ref={ref}
            tabIndex={x.tabIndex}
          />
        );
      case "checkbox":
        return (
          <>
            <span />
            <Checkbox
              name={x.field}
              onChange={props.onChange}
              value={props.getValue(x.field)}
              disabled={x.disabled || props.disabled}
              label={x.title}
              tabIndex={x.tabIndex}
            />
          </>
        );

      case "enum":
        return (
          <DropDownList
            name={x.field}
            data={
              props.parentProps[x.dataField]
                ? props.parentProps[x.dataField].data
                : []
            }
            textField={x.textField}
            dataItemKey={x.primaryField}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
          />
        );

      case "buttongroup":
        let enumData;
        if (x.EnumData) {
          enumData = x.EnumData;
        } else if (props.parentProps[x.dataField]) {
          enumData = props.parentProps[x.dataField].data;
        } else {
          enumData = [];
        }

        return (
          <ButtonGroup
            name={x.field}
            data={enumData}
            textField={x.textField}
            dataItemKey={x.primaryField}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
          />
        );

      case "radio":
        return (
          <RadioGroup
            name={x.field}
            data={
              props.parentProps[x.dataField]
                ? props.parentProps[x.dataField].data
                : []
            }
            textField={x.textField}
            dataItemKey={x.primaryField}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            ref={ref}
            tabIndex={x.tabIndex}
            layout={x.layout}
          />
        );
      case "date":
        return (
          <DatePicker
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min}
            max={x.max}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
          />
        );

      case "dateinput":
        return (
          <DateInput
            type="date"
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min}
            max={x.max}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            key={x.field}
          />
        );
      case "datetimeinput":
        return (
          <DateInput
            type="datetime"
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min}
            max={x.max}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            key={x.field}
          />
        );

      case "timeinput":
        return (
          <DateInput
            type="time"
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min}
            max={x.max}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            key={x.field}
          />
        );

      case "time":
        return (
          <TimePicker
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
          />
        );

      case "datetime":
        return (
          <DateTimePicker
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min}
            max={x.max}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
          />
        );

      case "daterange":
        return (
          <DateRangePicker
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min !== null ? x.min : undefined}
            max={x.max !== null ? x.max : undefined}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
          />
        );

      case 'daterangeinput':
        return (
          <DateRangeInput
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            min={x.min !== null ? x.min : undefined}
            max={x.max !== null ? x.max : undefined}
            ref={ref}
            tabIndex={x.tabIndex}
          />
        );

      case "numeric":
        return (
          <NumericTextBox
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            autofocus={x.autofocus}
            max={x.max}
            min={x.min}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            spinners={x.spinners}
            step={x.step}
          />
        );
      case "unitnumeric":
        const matches = x.title.match(/\[(.*)\]$/);
        const unit = matches && matches.length > 1 ? matches[1] : null;
        return (
          <UnitNumericTextBox
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            autofocus={x.autofocus}
            max={x.max}
            min={x.min}
            format={x.format}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            unit={unit}
            notUi={true}
            spinners={x.spinners ? x.spinners : false}
            step={x.step}
          />
        );

      case "container":
        return (
          <Picker
            textField={x.textField}
            valueField={x.valueField || x.textField}
            onChange={props.parentProps[x.onChange]}
            onSubmit={props.parentProps[x.onSubmit]}
            data={
              props.parentProps[x.data] ? props.parentProps[x.data].data : []
            }
            isLoading={
              props.parentProps[x.data] && props.parentProps[x.data].isLoading
                ? props.parentProps[x.data].isLoading
                : false
            }
            onSelect={props.onChange}
            selected={props.getValue(x.field)}
            schema={x.schema}
            total={
              props.parentProps[x.data] ? props.parentProps[x.data].total : 0
            }
            disabled={x.disabled || props.disabled}
            required={x.required}
            name={x.field}
            primaryField={x.primaryField}
            title={x.title}
            initFilter={x.initFilter}
            fixedFilter={x.fixedFilter}
            identif={x.Identif}
            autofocus={x.autofocus}
            detail={x.detail}
            editableDetail={x.editableDetail}
            onAdd={props.parentProps[x.onAdd]}
            parentProps={props.parentProps}
            ref={ref}
            tabIndex={x.tabIndex}
            placeholder={"Zvoľte požadovanú hodnotu"}
            onBlur={() => props.handleBlur(x, null)}
            withMeta={props.withMeta}
            db={props.db}
            reducer={x.reducer || props.reducer}
            akcFactory={props.akcFactory}
            allDataItem={x.allDataItem}
            sort={x.sort}
          />
        );

      case "grid":
        return <Grid {...x} parentProps={props.parentProps} ref={ref} />;

      case "basegrid":
        const data = props.getValue(x.field);
        return (
          <BaseGrid
            {...x}
            parentProps={props.parentProps}
            ref={ref}
            data={data}
            total={data ? data.length : 0}
          />
        );

      case "text":
      case "textarea":
        return (
          <Textarea
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            title={x.title}
            autofocus={x.autofocus}
            ref={ref}
            tabIndex={x.tabIndex}
            minLength={x.minLength}
            maxLength={x.maxLength}
            onBlur={() => props.handleBlur(x, null)}
            defaultExpanded={(x.defaultExpanded !== undefined && x.defaultExpanded !== null) ? x.defaultExpanded : (x.type === "textarea" ? true : false)}
            readOnly={x.readOnly}
            primaryFieldValue={props.getValue(props.primaryField)}
          />
        );

      case "autocomplete":
        return (
          <AutocompleteComponent
            name={x.field}
            data={
              props.parentProps[x.dataField]
                ? props.parentProps[x.dataField].data
                : []
            }
            textField={x.textField}
            onSelect={props.parentProps[x.onSelect]}
            selected={props.getValue(x.field)}
            disabled={x.disabled}
            onChange={props.parentProps[x.onChange]}
            ref={ref}
            primaryField={x.primaryField}
            tabIndex={x.tabIndex}
            isLoading={
              props.parentProps[x.data]
                ? props.parentProps[x.data].isLoading
                : false
            }
            onBlur={(value) => props.handleBlur(x, value)}
            valueField={x.valueField}
          />
        );

      case "multi":
        return (
          <MultiSelect
            name={x.field}
            data={x.EnumData ? x.EnumData : props.parentProps[x.dataField]?.data}
            //total={props.parentProps[x.dataField].total}
            textField={x.textField}
            value={props.getValue(x.field)}
            disabled={x.disabled}
            required={x.required}
            onChange={props.onChange}
            ref={ref}
            tabIndex={x.tabIndex}
            onBlur={() => props.handleBlur(x, null)}
            onFilterChange={x.onFilterChange}
          />
        );

      case "file":
      case "filepreview":
      case '26':
        return (
          <FileContent
            requests={props.requests}
            name={x.name || props.data[x.nameField]}
            data={x.data}
            url={x.url ? x.url.replace('{id}', props.data[x.idField] || 0) : null}
            mimeType={x.mimeType || props.data[x.mimeTypeField]}
            id={props.data[x.idField] || 0}
            hideButton={x.hideButton}
            imgWidth={x.imgWidth}
            pdfWidth={x.pdfWidth}
            pdfHeight={x.pdfHeight}
            src={props.data[x.field]}
          />
        );

      case "chat":
        return (
          <Conversational
            user={x.user}
            messages={props.parentProps[x.messages] || []}
            width={x.width}
            onSend={x.onSend}
            placeholder={x.placeholder}
          />
        );

      case 'multipicker':
      case '25':
        return (
          <MultiPicker
            textField={x.textField}
            valueField={x.valueField || x.textField}
            onChange={props.parentProps[x.onChange]}
            onSubmit={props.parentProps[x.onSubmit]}
            data={
              props.parentProps[x.data] ? props.parentProps[x.data].data : []
            }
            isLoading={
              props.parentProps[x.data]
                ? props.parentProps[x.data].isLoading
                : false
            }
            onSelect={props.onChange}
            selected={props.getValue(x.field)}
            schema={x.schema}
            total={
              props.parentProps[x.data] ? props.parentProps[x.data].total : 0
            }
            disabled={x.disabled || props.disabled}
            required={x.required}
            name={x.field}
            primaryField={x.primaryField}
            title={x.title}
            initFilter={x.initFilter}
            fixedFilter={x.fixedFilter}
            identif={x.Identif}
            autofocus={x.autofocus}
            detail={x.detail}
            editableDetail={x.editableDetail}
            onAdd={props.parentProps[x.onAdd]}
            parentProps={props.parentProps}
            ref={ref}
            tabIndex={x.tabIndex}
            placeholder={"Zvoľte požadovanú hodnotu"}
            onBlur={() => props.handleBlur(x, null)}
            withMeta={props.withMeta}
            db={props.db}
            reducer={x.reducer}
            akcFactory={props.akcFactory}
          />
        );

      case 'filedialog':
      case '27':
        return (
          <FileDialog
            isOpen={x.isOpen}
            requests={props.requests}
            name={x.name || props.data[x.nameField]}
            mimeType={x.mimeType || props.data[x.mimeTypeField]}
            id={props.data[x.idField]}
            imgWidth={x.imgWidth}
            pdfWidth={x.pdfWidth}
            pdfHeight={x.pdfHeight}
            src={props.data[x.field]}
          />
        );

      case "formdatatable":
      case "28":
        return (
          <FormDataTable
            detailData={props.data}
            schema={x.schema}
            primaryField={x.Identif}
            currentPage={pageIndex}
            myPage={page}
            frms={props.parentProps.frms.find(y => y.IFrm === x.Args?.[x.field]?.["IPodFrmNadr"]) ?? null}
            handleChangeFrm={props.parentProps.handleChangeFrm}
          />
        );

      case "string":
      case "password":
      case "email":
      default:
        return (
          <Input
            name={x.field}
            onChange={props.onChange}
            value={props.getValue(x.field)}
            disabled={x.disabled || props.disabled}
            required={x.required}
            autofocus={x.autofocus}
            minLength={x.minLength}
            maxLength={x.maxLength}
            pattern={x.pattern}
            validationMessage={x.validationMessage}
            ref={ref}
            tabIndex={x.tabIndex}
            type={
              (x.type === "password" && !x.showPassword) || x.type === "url"
                ? x.type
                : null
            }
            onBlur={() => props.handleBlur(x, null)}
            copyable={x.copyable}
          />
        );
    }
  };

  const buildCustomComponent = (x) => {
    const { componentFactory, data, schema } = props;
    const obj = {
      name: x.field,
      field: x,
      data,
      schema,
      buildField: buildField,
    };
    return (
      <React.Fragment key={x.field}>
        {componentFactory
          ? componentFactory(
            props.primaryField,
            x,
            props.getValue(x.field),
            props.onChange,
            obj
          )
          : x.customComponent}
      </React.Fragment>
    );
  };

  const buildField = (x, fieldClassName, page) => {
    if (x.Hidden) {
      return null;
    }
    const disabledClass = x.disabled ? "disabled-field" : "";
    const customClass = fieldClassName || "";
    const validationMessage = buildValidationMessage(x);
    let title = x.title;
    if (x.type === "unitnumeric") {
      const matches = x.title.match(/(.*)\[(.*)\]$/);
      title = matches && matches.length > 1 ? matches[1] : null;
    }
    return (
      <React.Fragment key={x.field}>
        <div
          title={x.tooltip}
          className={
            x.required && !x.disabled
              ? `k-form-field required form-${x.type} form-field-${x.field} ${customClass}`
              : `k-form-field form-${x.type === "text" ? "text-input" : x.type} form-field-${x.field} ${disabledClass} ${customClass}`
          }
          onMouseOver={(e) => { setOpenTooltip(true); setTooltipElement(e.currentTarget); }
          }
          onMouseOut={(e) => { setOpenTooltip(false); setTooltipElement(null); }
          }
        >
          {
            x.type === "checkbox" || x.type === "formdatatable" ? null : (
              <span className="detail-label">
                {x.validationIcon && (
                  <span className={`k-icon validation-image`}></span>
                )}
                {title}
              </span>
            )
          }
          {buildInput(x, page)}
          {x.tooltip && <div className="detail-tooltip">{x.tooltip}</div>}
          {x.customComponent}
        </div >
        <ValidationMessage
          required={x.required}
          recommended={x.recommended}
          disabled={x.disabled}
          validationMessage={validationMessage}
        />

      </React.Fragment >
    );
  };

  const buildValidationMessage = (x) => {
    if (x.validationMessage) {
      return x.validationMessage;
    }
    if (x.type === "email" && props.data && props.data[x.field]) {
      return "Nesprávny formát emailu";
    }
    if (
      x.type === "password" &&
      props.data[x.field] &&
      !new RegExp(window.config.passwordPattern).test(props.data[x.field])
    ) {
      return window.config.passwordErrMsg;
    }
    return null;
  };

  const buildPageClassName = (pageIndexNew) => {
    if (pageIndexNew > 0) {
      if (pageIndex === pageIndexNew) {
        const scrollableElement =
          document.getElementsByClassName("scrollable-navbar")[0];
        return `sidebar-${pageIndexNew} col-4 ${props.sidebarHorizontal ? "sidebar-horizontal" : ""
          } ${scrollableElement &&
            scrollableElement.scrollHeight > scrollableElement.clientHeight
            ? ""
            : "scrollable-expanded-page"
          }`;
      } else {
        return `sidebar-${pageIndexNew} compact col-1 ${props.sidebarHorizontal ? "sidebar-horizontal" : ""
          }`;
      }
    } else {
      return "page col";
    }
  };

  const buildColumnClassName = (column) => {
    return column[0] && column[0].className
      ? `col-md ${column[0].className}`
      : "col-md";
  };

  const buildPage = (page, index) => {
    return page.page.map((row, rowIndex) => (
      <div key={rowIndex} className="row">
        {row.map((column, columnIndex) => (
          <div key={columnIndex} className={buildColumnClassName(column)}>
            {column.map((field) => {
              if (field.customComponent) {
                return buildCustomComponent(field);
              } else if (field.title) {
                return <div className="detail-subtitle">{field.title}</div>;
              } else if (field.line) {
                return <div className={`detail-line-${field.line}`}><hr /></div>;
              } else {
                let x = props.schema.find((f) => f.field === field.field);
                return x
                  ? buildField(
                    x,
                    [field.className, x.className].filter((s) => s).join(" "),
                    index
                  )
                  : null;
              }
            })}
          </div>
        ))}
      </div>
    ));
  };

  const buildTabsForm = () => {
    const pages = ui.filter((page) => page.IZobraz !== 3);
    return (
      <div className="detail">
        <TabStrip
          selected={tabIndex}
          onSelect={handleSelect}
          tabPosition={props.args?.tabPosition}
          className={"page col"}
        >
          {pages.map((page, pageIndex) => {
            return (
              page.IZalTyp === 2 && (
                <TabStripTab
                  key={pageIndex}
                  title={page.title}
                  disabled={page.IZobraz === 2}
                >
                  <div className="detail row">{buildPage(page, pageIndex)}</div>
                </TabStripTab>
              )
            );
          })}
        </TabStrip>
        {pages.map((page, pageIndex) => {
          return (
            page.IZalTyp === 1 && (
              <div
                key={pageIndex}
                className={
                  buildPageClassName(pageIndex) + " " + buildPageCustomClass(pageIndex)
                }
              >
                {buildSideBarItem(pageIndex)}
                {buildPage(page, pageIndex)}
              </div>
            )
          );
        })}
      </div>
    );
  };

  const buildSideBarItem = (pageIndexNew) => {
    const iconOpen = props.sidebarHorizontal ? "down" : "left";
    const iconClose = props.sidebarHorizontal ? "up" : "right";
    return (
      pageIndexNew > 0 && (
        <span
          className="sidebar-header"
          onClick={(event) => handleClick(event, pageIndexNew)}
        >
          <h2>{buildPageTitle(pageIndexNew)}</h2>
          <span
            className={`sidebar-toggle k-icon ${pageIndex !== pageIndexNew
              ? "compact k-i-arrow-chevron-" + iconOpen
              : "k-i-arrow-chevron-" + iconClose
              }`}
          />
        </span>
      )
    );
  };

  const buildDefaultForm = () => {
    return ui.map((page, pageIndex) => (
      <div
        key={pageIndex}
        className={
          buildPageClassName(pageIndex) + " " + buildPageCustomClass(pageIndex)
        }
      >
        {buildSideBarItem(pageIndex)}
        {props.title && pageIndex === 0 && (
          <div className="row">
            <div className="col-md">
              <div className="detail-title">{props.title}</div>
            </div>
          </div>
        )}
        {buildPage(page, pageIndex)}
      </div>
    ));
  };

  const buildHorizontalForm = () => {
    const sidebarColumns = ui.filter((page, pageIndex) => pageIndex > 0);
    const uiColumns = ui.filter((page, pageIndex) => pageIndex === 0);

    let form = [
      uiColumns.map((page, index) => (
        <div
          key={index}
          className={
            buildPageClassName(index) +
            " " +
            buildPageCustomClass(index)
          }
        >
          {props.title && index === 0 && (
            <div className="row">
              <div className="col-md">
                <div className="detail-title">{props.title}</div>
              </div>
            </div>
          )}
          {buildPage(page, index)}
        </div>
      )),
      <div className={`side-column ${pageIndex ? `column-expanded` : ``}`}>
        {sidebarColumns.map((page, index) => (
          <div
            key={index}
            className={
              buildPageClassName(index + 1) +
              " " +
              buildPageCustomClass(index + 1)
            }
          >
            {buildSideBarItem(index + 1)}
            {buildPage(page, index)}
          </div>
        ))}
      </div>,
    ];
    return form;
  };

  const buildForm = () => {
    if (ui) {
      if (ui && ui.find((x) => x.IZalTyp === 2)) {
        return buildTabsForm();
      } else {
        return (
          <div className="detail">
            {props.sidebarHorizontal
              ? buildHorizontalForm()
              : buildDefaultForm()}
          </div>
        );
      }
    } else {
      return (
        <div className="detail">
          <div className="col-md page">
            {props.title && <div className="row"><div className="col-md"> <div className="detail-title">{props.title}</div></div></div>}
            <div className="row">
              {props.schema.map((x) => buildField(x, x.className))}
            </div>
          </div>
        </div>
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const form = useMemo(() => buildForm(), [ui, pageIndex, tabIndex, props])

  const primaryField = props.primaryField.split("-").pop();
  return (
    <div className="detail-wrapper">
      <ValidationAlert
        dataInvalid={props.dataInvalid}
        invalidFields={props.invalidFields}
      />
      {!props.toolbarHidden && (
        <Toolbar
          disabled={props.disabled}
          akcDisabled={props.akcDisabled}
          akcHidden={props.akcHidden}
          primaryField={primaryField}
          previous={props.previous}
          next={props.next}
          disabledNext={props.disabledNext}
          disabledPrevious={props.disabledPrevious}
          currentEl={props.currentEl}
          onBack={props.onBack}
          isPicker={props.isPicker}
          onGet={props.onGet}
          onSubmit={props.handleSubmit}
          hiddenBack={props.hiddenBack}
          hiddenSave={props.hiddenSave}
          hiddenPrint={props.primaryField !== "ZiadPrist-I_ZIADOST"}
          onPrint={props.onPrint}
          withMeta={props.withMeta}
        >
          {props.toolbarActions}
        </Toolbar>
      )}

      {!props.isPicker ? (
        <form id={primaryField} className="k-form-inline" >
          {props.schema && form}
          {props.children}
          <LoadingPanel isLoading={props.isLoading} showAbort={props.showAbort} />
        </form>
      ) : (
        <div id={primaryField} className="k-form-inline">
          {props.schema && form}
          {props.children}
          <LoadingPanel isLoading={props.isLoading} showAbort={props.showAbort} />
        </div>
      )}
      <Tooltip
        ref={tooltipRef}
        position={"top"}
        open={openTooltip}
        targetElement={tooltipElement}
        anchorElement={"target"}
        parentTitle={false}
      />
    </div >
  );
});

Detail.propTypes = {
  schema: PropTypes.array,
  data: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  getValue: PropTypes.func,
  onUpload: PropTypes.func,
  parentProps: PropTypes.object,
  disabled: PropTypes.bool,
  akcDisabled: PropTypes.number,
  akcHidden: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  ui: PropTypes.array,
  args: PropTypes.object,
  toolbarActions: PropTypes.any,
  primaryField: PropTypes.string.isRequired,
  toolbarHidden: PropTypes.bool,
  autofocus: PropTypes.bool,
  next: PropTypes.func.isRequired,
  previous: PropTypes.func.isRequired,
  disabledNext: PropTypes.bool,
  disabledPrevious: PropTypes.bool,
  currentEl: PropTypes.element,
  onBack: PropTypes.func.isRequired,
  onPrint: PropTypes.func,
  initFilter: PropTypes.object,
  fixedFilter: PropTypes.object,
  onAdd: PropTypes.func,
  onGet: PropTypes.func,
  isPicker: PropTypes.bool,
  hiddenBack: PropTypes.bool,
  applyFilter: PropTypes.func,
  title: PropTypes.string,
  pages: PropTypes.arrayOf(PropTypes.object),
  hiddenSave: PropTypes.bool,
  sidebarHorizontal: PropTypes.bool,
  withMeta: PropTypes.bool,
  componentFactory: PropTypes.func,
  db: PropTypes.string,
};

export default withRequests(Detail);